import React from 'react'
import { Currency, Price } from '@uniswap/sdk-core'
import StepCounter from 'components/InputStepCounter/InputStepCounter'
import { RowBetween } from 'components/Row'
import { useActiveWeb3React } from 'hooks'
import { wrappedCurrency } from 'utils/wrappedCurrency'
import { SmallButton } from 'pages/AddLiquidity/styled'
import { formatPriceString } from 'utils/formatTokenAmount'
import { nearestUsableTick, TickMath } from '@uniswap/v3-sdk'
import { BigNumber } from 'ethers'
import QuestionHelper from 'components/QuestionHelper'
import { formatUnits } from 'ethers/lib/utils'

// currencyA is the base token
export default function RangeSelector({
  priceLower,
  priceUpper,
  onLeftRangeInput,
  onRightRangeInput,
  getDecrementLower,
  getIncrementLower,
  getDecrementUpper,
  getIncrementUpper,
  currencyA,
  currencyB,
  feeAmount,
}: {
  priceLower?: Price
  priceUpper?: Price
  getDecrementLower: () => string
  getIncrementLower: () => string
  getDecrementUpper: () => string
  getIncrementUpper: () => string
  onLeftRangeInput: (typedValue: string) => void
  onRightRangeInput: (typedValue: string) => void
  currencyA?: Currency | null
  currencyB?: Currency | null
  feeAmount?: number
}) {
  const { chainId } = useActiveWeb3React()
  const tokenA = wrappedCurrency(currencyA ?? undefined, chainId)
  const tokenB = wrappedCurrency(currencyB ?? undefined, chainId)
  const isSorted = tokenA && tokenB && tokenA?.address !== tokenB?.address && tokenA.sortsBefore(tokenB)

  const leftPrice = isSorted ? priceLower : priceUpper?.invert()
  const rightPrice = isSorted ? priceUpper : priceLower?.invert()

  const handleInfiniteRange = () => {
    // Ensure that necessary values are available before proceeding
    if (!currencyA || !currencyB || !feeAmount) return

    // Define the maximum threshold price (10^36) as a cap to prevent excessively large values
    const MAX_THRESHOLD = BigNumber.from('10').pow(36)

    // Determine the tick spacing based on the fee tier of the pool
    // Each pool has different tick spacing constraints:
    // - 0.01% (10,000 fee) → tickSpacing = 200
    // - 0.05% (3,000 fee) → tickSpacing = 60
    // - 0.3% (500 fee) → tickSpacing = 10
    // - 1% (100 fee) → tickSpacing = 1
    const tickSpacing =
      feeAmount === 10000 ? 200 : feeAmount === 3000 ? 60 : feeAmount === 500 ? 10 : feeAmount === 100 ? 1 : 1 // Default to 1 if feeAmount is undefined (failsafe)

    // Get the nearest usable ticks for min and max based on tickSpacing
    const minTick = nearestUsableTick(TickMath.MIN_TICK, tickSpacing)
    const maxTick = nearestUsableTick(TickMath.MAX_TICK, tickSpacing)

    // Compute the sqrtPriceX96 values for the given min and max ticks
    // sqrtPriceX96 represents the square root of the price in Q96 format (fixed-point representation)
    const sqrtRatioX96Min = TickMath.getSqrtRatioAtTick(minTick)
    const sqrtRatioX96Max = TickMath.getSqrtRatioAtTick(maxTick)

    // Convert sqrtPriceX96 values from JSBI (used in Uniswap SDK) to BigNumber (used in ethers.js)
    const sqrtRatioX96MinBN = BigNumber.from(sqrtRatioX96Min.toString())
    const sqrtRatioX96MaxBN = BigNumber.from(sqrtRatioX96Max.toString())

    // Ensure correct token order for price calculations
    const currencyASorted = isSorted ? currencyA : currencyB
    const currencyBSorted = isSorted ? currencyB : currencyA

    // Determine the number of decimals for the tokens (default to 18 decimals like ETH)
    const minDecimals = currencyASorted?.decimals ?? 18
    const maxDecimals = currencyBSorted?.decimals ?? 18

    // Convert sqrtPriceX96 values into normal price values using the formula:
    // P = (sqrtPriceX96 / 2^96)²
    // Equivalent to:
    // P = (sqrtPriceX96 * sqrtPriceX96) / 2^192
    const minPrice = sqrtRatioX96MinBN.mul(sqrtRatioX96MinBN).div(BigNumber.from(2).pow(192))
    const maxPrice = sqrtRatioX96MaxBN.mul(sqrtRatioX96MaxBN).div(BigNumber.from(2).pow(192))

    // Ensure the minimum price is a valid and non-zero value
    const safeMinPrice = minPrice.isZero()
      ? formatUnits(BigNumber.from(1), minDecimals) // If zero, set a minimal valid value based on token decimals
      : formatUnits(minPrice, minDecimals) // Otherwise, format the price properly

    // Ensure the maximum price is within a reasonable limit
    const safeMaxPrice = maxPrice.gte(BigNumber.from('10').pow(18)) // Check if maxPrice is greater than 10^18
      ? formatUnits(MAX_THRESHOLD, maxDecimals) // If so, cap it at MAX_THRESHOLD (10^36)
      : formatUnits(maxPrice.mul(BigNumber.from(999)).div(BigNumber.from(1000)), maxDecimals) // Otherwise, apply 99.9% adjustment

    // Update the UI inputs with the computed price values
    onLeftRangeInput(safeMinPrice)
    onRightRangeInput(safeMaxPrice)
  }

  return (
    <>
      <RowBetween>
        <div style={{ width: 'calc(50% - 8px)', display: 'flex' }}>
          <SmallButton
            active={
              formatPriceString(leftPrice?.toSignificant(5)) === '≈0' &&
              formatPriceString(rightPrice?.toSignificant(5)) === '∞'
            }
            onClick={() => {
              handleInfiniteRange()
            }}
            style={{ width: 'calc(100% - 18px)' }}
          >
            Max Tick Range (Auto){' '}
          </SmallButton>
          <QuestionHelper text="Selecting ‘Max Tick Range (Auto)’ sets the price range to the maximum possible values allowed by Hyperswap V3 for this pool. Due to tick spacing constraints, the actual maximum price is slightly below infinity. Even if you manually enter a higher value, Hyperswap will still cap it at the highest allowed tick, ensuring no additional impact on liquidity distribution." />
        </div>

        <SmallButton
          onClick={() => {
            onLeftRangeInput('')
            onRightRangeInput('')
          }}
          style={{ height: '100%' }}
        >
          Reset Price Range
        </SmallButton>
      </RowBetween>
      <RowBetween>
        <StepCounter
          value={leftPrice?.toSignificant(5) ?? ''}
          onUserInput={onLeftRangeInput}
          width="48%"
          decrement={isSorted ? getDecrementLower : getIncrementUpper}
          increment={isSorted ? getIncrementLower : getDecrementUpper}
          feeAmount={feeAmount}
          label={leftPrice ? `${currencyB?.symbol}` : '-'}
          title={'Min Price'}
          tokenA={currencyA?.symbol}
          tokenB={currencyB?.symbol}
        />
        <StepCounter
          value={rightPrice?.toSignificant(5) ?? ''}
          onUserInput={onRightRangeInput}
          width="48%"
          decrement={isSorted ? getDecrementUpper : getIncrementLower}
          increment={isSorted ? getIncrementUpper : getDecrementLower}
          feeAmount={feeAmount}
          label={rightPrice ? `${currencyB?.symbol}` : '-'}
          tokenA={currencyA?.symbol}
          tokenB={currencyB?.symbol}
          title={'Max Price'}
        />
      </RowBetween>
    </>
  )
}
